import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Archive, Copy, Eye, Loader2, Plus, RotateCcw, Save, Star, Trash2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const SAMPLE = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=900&q=80";
const RATIO = { "1:1": 1, "4:3": 4 / 3, "3:4": 3 / 4, "16:9": 16 / 9 };
const PAGE_MM = 180; // printable width of an A4 quotation page

export default function QuotationTemplates() {
  const { user } = useAuth();
  const [list, setList] = useState({ templates: [], can_manage: false, default_id: "" });
  const [options, setOptions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [img, setImg] = useState(null);
  const [name, setName] = useState("");
  const [base, setBase] = useState("premium");
  const [busy, setBusy] = useState("");

  const load = useCallback(async (keepId) => {
    try {
      const [{ data: l }, { data: o }] = await Promise.all([
        api.get("/quotation-templates?include_archived=true"),
        api.get("/quotation-templates/options"),
      ]);
      setList(l);
      setOptions(o);
      const pick = l.templates.find((t) => t.id === (keepId || selected?.id))
        || l.templates.find((t) => t.is_default) || l.templates[0];
      if (pick) {
        setSelected(pick);
        setImg({ ...o.defaults, ...pick.image });
        setName(pick.name);
        setBase(pick.base);
      }
    } catch (e) {
      toast.error(apiError(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setImg((s) => {
    const next = { ...s, [k]: v };
    // Presets fill in the actual numbers so the form and the preview match the PDF.
    if (k === "preset" && options?.presets?.[v]) {
      Object.assign(next, options.presets[v]);
    }
    if ((k === "width" || k === "unit" || k === "aspect_ratio" || k === "maintain_aspect")
      && next.maintain_aspect && RATIO[next.aspect_ratio]) {
      next.height = Math.round((Number(next.width) / RATIO[next.aspect_ratio]) * 100) / 100;
    }
    if (k === "width" || k === "height" || k === "unit") next.preset = "custom";
    return next;
  });

  const pick = (t) => {
    setSelected(t);
    setImg({ ...options.defaults, ...t.image });
    setName(t.name);
    setBase(t.base);
  };

  const run = async (key, fn, message, keepId) => {
    setBusy(key);
    try {
      const r = await fn();
      if (message) toast.success(message);
      await load(keepId ?? r?.data?.id ?? selected?.id);
      return r;
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const openPdfPreview = async () => {
    setBusy("preview");
    try {
      const r = await api.post("/quotation-templates/preview",
        { base, image: img }, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) {
      const detail = e?.response?.data instanceof Blob
        ? await e.response.data.text().then((t) => {
          try { return JSON.parse(t).detail; } catch { return t; }
        })
        : apiError(e);
      toast.error(detail || "Could not build the preview");
    } finally {
      setBusy("");
    }
  };

  // live preview geometry, in the same proportion the PDF will use
  const geometry = useMemo(() => {
    if (!img) return null;
    const width = Number(img.width || 70);
    const unit = img.unit || "mm";
    const widthMm = unit === "mm" ? width
      : unit === "px" ? width * 0.2646
        : (PAGE_MM * width) / 100;
    const ratio = img.maintain_aspect ? RATIO[img.aspect_ratio] : null;
    const heightMm = ratio ? widthMm / ratio
      : (img.unit === "mm" ? Number(img.height || 45) : Number(img.height || 45) * 0.2646);
    const perRow = Math.max(1, Math.min(Number(img.per_row || 1), 4));
    const cellMm = Math.min(widthMm, PAGE_MM / perRow);
    return {
      widthPct: Math.min((cellMm / PAGE_MM) * 100, 100),
      aspect: `${cellMm} / ${heightMm}`,
      widthMm: Math.round(cellMm * 10) / 10,
      heightMm: Math.round(heightMm * 10) / 10,
      perRow,
    };
  }, [img]);

  if (!options || !img) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const manage = list.can_manage;
  const objectFit = img.fit === "cover" ? "cover" : img.fit === "original" ? "none" : "contain";

  return (
    <div data-testid="template-editor">
      <PageHeader
        title="Quotation Template Editor"
        subtitle={manage
          ? "Set the product image size, shape, spacing and print quality, preview it, then make it the default."
          : "View only — the Owner or a Director sets the quotation template."}
        actions={manage && (
          <Button size="sm" className="gap-1.5" data-testid="new-template-button"
            onClick={() => {
              const n = window.prompt("Name for the new template");
              if (n) run("new", () => api.post("/quotation-templates",
                { name: n, base, image: img }), "Template created");
            }}>
            <Plus className="h-4 w-4" /> New template
          </Button>
        )}
      />

      <div className="grid gap-4 p-4 sm:p-6 lg:grid-cols-[280px_1fr]">
        <div className="space-y-2" data-testid="template-list">
          {list.templates.map((t) => (
            <button key={t.id} type="button" data-testid={`template-item-${t.id}`}
              onClick={() => pick(t)}
              className={`surface w-full p-3 text-left transition-colors ${
                selected?.id === t.id ? "border-primary/60 bg-secondary/50" : "hover:bg-secondary/30"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium">{t.name}</span>
                {t.is_default && (
                  <Badge className="shrink-0 text-[9px]" data-testid={`template-default-${t.id}`}>
                    DEFAULT
                  </Badge>
                )}
              </div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {t.base} • v{t.version} • {t.status}
                {t.is_original ? " • original" : ""}
              </div>
            </button>
          ))}
        </div>

        <div className="space-y-4">
          <div className="surface p-4">
            <div className="overline mb-3">Live preview</div>
            <div className="rounded border border-border bg-white p-4">
              <div className="mx-auto" style={{ maxWidth: 620 }}>
                <div className="mb-2 text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  1. Family Water Slide FS-1200
                </div>
                <div className="flex flex-wrap gap-2"
                  style={{
                    justifyContent: img.align === "center" ? "center"
                      : img.align === "right" ? "flex-end" : "flex-start",
                    marginTop: `${img.margin_top || 0}mm`,
                    marginBottom: `${img.margin_bottom || 0}mm`,
                    marginLeft: `${img.margin_left || 0}mm`,
                    marginRight: `${img.margin_right || 0}mm`,
                  }}>
                  {Array.from({ length: geometry.perRow }).map((_, i) => (
                    <div key={i} data-testid={`preview-image-${i}`}
                      style={{
                        width: `calc(${geometry.widthPct}% - ${geometry.perRow > 1 ? 8 : 0}px)`,
                        aspectRatio: geometry.aspect,
                        border: img.border ? `${img.border_width || 0.6}px solid #C9CFD8` : "none",
                        borderRadius: `${img.border_radius || 0}px`,
                        overflow: "hidden",
                        background: "#FFFFFF",
                      }}>
                      <img src={SAMPLE} alt="product sample"
                        style={{ width: "100%", height: "100%", objectFit, objectPosition: "center" }} />
                    </div>
                  ))}
                </div>
                <div className="text-[11px] leading-relaxed text-slate-600">
                  <div className="font-medium text-slate-800">Fibreglass water slide, 12 ft</div>
                  <div>Qty: 2 Nos &nbsp; Rate: 16,980.00 &nbsp; GST: 18%</div>
                  <div>Line Total: INR 33,960.00</div>
                </div>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] text-muted-foreground" data-testid="preview-dimensions">
                Each image prints about {geometry.widthMm} × {geometry.heightMm} mm •{" "}
                {geometry.perRow} per row • {img.quality} quality
              </span>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={openPdfPreview}
                disabled={busy === "preview"} data-testid="pdf-preview-button">
                {busy === "preview"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Eye className="h-3.5 w-3.5" />}
                Preview real PDF
              </Button>
            </div>
          </div>

          <div className="surface p-4">
            <div className="overline mb-3">Product image settings</div>
            <fieldset disabled={!manage} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Template name">
                <Input data-testid="template-name-input" value={name}
                  onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="Layout style">
                <Select value={base} onValueChange={setBase}>
                  <SelectTrigger data-testid="template-base-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.bases.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Images beside or above the text">
                <Select value={img.layout} onValueChange={(v) => set("layout", v)}>
                  <SelectTrigger data-testid="template-layout-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="above">Above the product details</SelectItem>
                    <SelectItem value="beside">Beside the product details</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Size">
                <Select value={img.preset} onValueChange={(v) => set("preset", v)}>
                  <SelectTrigger data-testid="template-preset-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (as it is today)</SelectItem>
                    <SelectItem value="small">Small</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="large">Large</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Measure in">
                <Select value={img.unit} onValueChange={(v) => set("unit", v)}>
                  <SelectTrigger data-testid="template-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mm">Millimetre (print)</SelectItem>
                    <SelectItem value="px">Pixel</SelectItem>
                    <SelectItem value="percent">Percent of page width</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={`Width (${img.unit})`}>
                <Input type="number" data-testid="template-width-input" value={img.width}
                  onChange={(e) => set("width", Number(e.target.value))} />
              </Field>

              <Field label="Keep the shape (no stretching)">
                <div className="flex h-9 items-center gap-2">
                  <Switch data-testid="template-aspect-toggle" checked={!!img.maintain_aspect}
                    onCheckedChange={(v) => set("maintain_aspect", v)} />
                  <span className="text-xs text-muted-foreground">
                    {img.maintain_aspect ? "Height follows the ratio" : "Height set by hand"}
                  </span>
                </div>
              </Field>
              <Field label="Shape (aspect ratio)">
                <Select value={img.aspect_ratio} onValueChange={(v) => set("aspect_ratio", v)}
                  disabled={!img.maintain_aspect}>
                  <SelectTrigger data-testid="template-ratio-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.ratios.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r === "custom" ? "Custom (use height below)" : r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={`Height (${img.unit})`}>
                <Input type="number" data-testid="template-height-input" value={img.height}
                  disabled={img.maintain_aspect && img.aspect_ratio !== "custom"}
                  onChange={(e) => set("height", Number(e.target.value))} />
              </Field>

              <Field label="Alignment">
                <Select value={img.align} onValueChange={(v) => set("align", v)}>
                  <SelectTrigger data-testid="template-align-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.aligns.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="How the photo fills the box">
                <Select value={img.fit} onValueChange={(v) => set("fit", v)}>
                  <SelectTrigger data-testid="template-fit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contain">Contain — whole photo, never cropped</SelectItem>
                    <SelectItem value="cover">Cover — fills the box, edges trimmed</SelectItem>
                    <SelectItem value="original">Original proportions</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Images per row">
                <Select value={String(img.per_row)} onValueChange={(v) => set("per_row", Number(v))}>
                  <SelectTrigger data-testid="template-perrow-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              {[
                ["margin_top", "Space above (mm)"],
                ["margin_bottom", "Space below (mm)"],
                ["margin_left", "Space left (mm)"],
                ["margin_right", "Space right (mm)"],
              ].map(([k, label]) => (
                <Field key={k} label={label}>
                  <Input type="number" data-testid={`template-${k}-input`} value={img[k] ?? 0}
                    onChange={(e) => set(k, Number(e.target.value))} />
                </Field>
              ))}

              <Field label="Border">
                <div className="flex h-9 items-center gap-2">
                  <Switch data-testid="template-border-toggle" checked={!!img.border}
                    onCheckedChange={(v) => set("border", v)} />
                  <span className="text-xs text-muted-foreground">
                    {img.border ? "Thin line around each photo" : "No border"}
                  </span>
                </div>
              </Field>
              <Field label={`Rounded corners (${img.border_radius || 0})`}>
                <Slider data-testid="template-radius-slider" min={0} max={24} step={1}
                  value={[Number(img.border_radius || 0)]}
                  onValueChange={([v]) => set("border_radius", v)} />
              </Field>
              <Field label="Print quality">
                <Select value={img.quality} onValueChange={(v) => set("quality", v)}>
                  <SelectTrigger data-testid="template-quality-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.qualities.map((qy) => (
                      <SelectItem key={qy} value={qy}>
                        {qy.toUpperCase()} — {options.quality_notes[qy]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </fieldset>

            {manage && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" className="gap-1.5" data-testid="template-save-button"
                  disabled={busy === "save"}
                  onClick={() => run("save", () => api.put(`/quotation-templates/${selected.id}`,
                    { name, base, image: img }), "Template saved", selected.id)}>
                  {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Save className="h-3.5 w-3.5" />}
                  Save
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5"
                  data-testid="template-set-default-button" disabled={selected.is_default}
                  onClick={() => run("default",
                    () => api.post(`/quotation-templates/${selected.id}/default`),
                    `${selected.name} is now the default`, selected.id)}>
                  <Star className="h-3.5 w-3.5" /> Make default
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5"
                  data-testid="template-duplicate-button"
                  onClick={() => run("dup",
                    () => api.post(`/quotation-templates/${selected.id}/duplicate`),
                    "Copy created")}>
                  <Copy className="h-3.5 w-3.5" /> Duplicate
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5"
                  data-testid="template-reset-previous-button"
                  onClick={() => run("reset",
                    () => api.post(`/quotation-templates/${selected.id}/reset?to=previous`),
                    "Went back to the previous version", selected.id)}>
                  <RotateCcw className="h-3.5 w-3.5" /> Undo last save
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5"
                  data-testid="template-reset-default-button"
                  onClick={() => run("resetd",
                    () => api.post(`/quotation-templates/${selected.id}/reset?to=default`),
                    "Back to the original settings", selected.id)}>
                  Reset to original
                </Button>
                {selected.status === "active" ? (
                  <Button size="sm" variant="outline" className="gap-1.5"
                    data-testid="template-archive-button" disabled={selected.is_default}
                    onClick={() => run("arch",
                      () => api.post(`/quotation-templates/${selected.id}/status`,
                        { status: "archived" }), "Template archived", selected.id)}>
                    <Archive className="h-3.5 w-3.5" /> Archive
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="gap-1.5"
                    data-testid="template-restore-button"
                    onClick={() => run("rest",
                      () => api.post(`/quotation-templates/${selected.id}/status`,
                        { status: "active" }), "Template restored", selected.id)}>
                    <RotateCcw className="h-3.5 w-3.5" /> Restore
                  </Button>
                )}
                {!selected.is_default && !selected.is_original && (
                  <Button size="sm" variant="ghost" className="gap-1.5 text-destructive"
                    data-testid="template-delete-button"
                    onClick={() => run("del",
                      () => api.delete(`/quotation-templates/${selected.id}`),
                      "Template removed", "")}>
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </Button>
                )}
              </div>
            )}
            <p className="mt-3 text-[11px] text-muted-foreground">
              Product details, prices and totals are never changed by these settings. Items
              without a photo simply show their details, and photos are never stretched.
              {user?.role && !manage ? " Ask a Director to change the template." : ""}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
